<template>
  <div class="virtual-scroller-wrapper">
    <!-- Gerçek scrollable container -->
    <div class="virtual-scroller" ref="scroller" @scroll="handleScroll">
      <!-- Toplam yüksekliği simüle eden içerik -->
      <div class="scroller-content" :style="{ height: `${totalSize}px` }">
        <!-- İçeriği konumlandıran görünür öğeler container'ı -->
        <div class="items-container" :style="{ top: `${offsetTop}px` }">
          <!-- Her bir satır öğesi -->
          <div v-for="(item, index) in visibleItems" :key="getItemKey(item, index)" class="virtual-item">
            <slot :item="item" :index="startIndex + index" />
          </div>
        </div>
      </div>

      <!-- Yükleme ve boş durum göstergeleri -->
      <div v-if="showEmptyPlaceholder" class="virtual-scroller-empty">
        <slot name="empty">
          <div class="empty-placeholder">{{ emptyText }}</div>
        </slot>
      </div>
      <div v-if="loading" class="virtual-scroller-loading">
        <slot name="loading">
          <div class="loading-indicator">{{ $t('table.loading') }}</div>
        </slot>
      </div>
    </div>

    <!-- Popup bileşenlerinin (takvim, tooltip vb.) doğru konumlanması için container -->
    <div class="virtual-scroller-portals"></div>
  </div>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: 'VirtualScroller',
  props: {
    items: {
      type: Array,
      required: true
    },
    itemSize: {
      type: Number,
      required: true
    },
    keyField: {
      type: String,
      default: ''
    },
    buffer: {
      type: Number,
      default: 5
    },
    loading: {
      type: Boolean,
      default: false
    },
    direction: {
      type: String,
      default: 'vertical',
      validator: (value) => ['vertical', 'horizontal'].includes(value)
    },
    emptyText: {
      type: String,
      default: 'Gösterilecek veri bulunamadı'
    }
  },
  data() {
    return {
      startIndex: 0,
      endIndex: 0,
      offsetTop: 0,
      viewportHeight: 0,
      scrollTop: 0,
      hasInitialized: false,
      isScrolling: false
    };
  },
  computed: {
    visibleCount() {
      return Math.ceil(this.viewportHeight / this.itemSize) + this.buffer * 2;
    },
    totalSize() {
      return this.items.length * this.itemSize;
    },
    visibleItems() {
      if (!this.items.length) return [];
      return this.items.slice(this.startIndex, this.endIndex + 1);
    },
    showEmptyPlaceholder() {
      return !this.loading && this.items.length === 0;
    }
  },
  methods: {
    getItemKey(item, index) {
      return this.keyField && item[this.keyField] ? item[this.keyField] : `item-${this.startIndex + index}`;
    },
    handleScroll() {
      this.scrollTop = this.$refs.scroller.scrollTop;

      // Scroll performansı için debounce kullanımı
      this.isScrolling = true;
      this.updateVisibleItemsImmediate();
      this.debouncedEndScroll();

      // Scroll olayını yukarı iletiyoruz, böylece üst bileşenler scroll durumunu takip edebilir
      this.$emit('scroll', {
        scrollTop: this.scrollTop,
        scrollHeight: this.$refs.scroller.scrollHeight,
        clientHeight: this.$refs.scroller.clientHeight,
        atStart: this.scrollTop <= 0,
        atEnd: Math.ceil(this.scrollTop + this.$refs.scroller.clientHeight) >= this.$refs.scroller.scrollHeight,
        isScrolling: this.isScrolling
      });
    },
    updateVisibleItemsImmediate() {
      const startIndex = Math.max(0, Math.floor(this.scrollTop / this.itemSize) - this.buffer);
      const endIndex = Math.min(
        this.items.length - 1,
        startIndex + this.visibleCount - 1
      );

      if (startIndex !== this.startIndex || endIndex !== this.endIndex) {
        this.startIndex = startIndex;
        this.endIndex = endIndex;
        this.offsetTop = startIndex * this.itemSize;
      }
    },
    // Öğeler ve görünüm güncellendiğinde kullanılabilecek daha geniş bir güncelleme
    updateVisibleItems() {
      this.updateViewportHeight();
      this.updateVisibleItemsImmediate();
    },
    updateViewportHeight() {
      if (this.$refs.scroller) {
        this.viewportHeight = this.$refs.scroller.clientHeight;
      }
    },
    scrollToItem(index, behavior = 'auto') {
      if (index >= 0 && index < this.items.length) {
        this.$refs.scroller.scrollTo({
          top: index * this.itemSize,
          behavior: behavior // 'auto', 'smooth'
        });
      }
    },
    scrollToOffset(offset, behavior = 'auto') {
      if (this.$refs.scroller) {
        this.$refs.scroller.scrollTo({
          top: offset,
          behavior: behavior // 'auto', 'smooth'
        });
      }
    },
    getScrollState() {
      if (!this.$refs.scroller) return null;

      return {
        scrollTop: this.$refs.scroller.scrollTop,
        startIndex: this.startIndex,
        endIndex: this.endIndex
      };
    },
    restoreScrollState(state) {
      if (!state || !this.$refs.scroller) return;

      this.$nextTick(() => {
        // Scroll pozisyonunu geri yükle
        if (typeof state.scrollTop === 'number') {
          this.$refs.scroller.scrollTop = state.scrollTop;

          // Start ve end index'leri de geri yükle
          if (typeof state.startIndex === 'number' && typeof state.endIndex === 'number') {
            this.startIndex = state.startIndex;
            this.endIndex = state.endIndex;
            this.offsetTop = this.startIndex * this.itemSize;
          }

          // Scroll durumunu güncelle
          this.scrollTop = state.scrollTop;
          this.updateVisibleItemsImmediate();
        }
      });
    },
    // Listeyi yenileyin ve görünür öğeleri güncelleyin
    refresh() {
      this.$nextTick(() => {
        this.updateVisibleItems();
      });
    },
    onEndScroll() {
      this.isScrolling = false;
      this.$emit('scroll-end', this.getScrollState());
    },
    fixAntdPortals() {
      // Ant Design'ın Portal bileşenlerini doğru ele almak için
      if (typeof document !== 'undefined') {
        // DatePicker ve diğer popup elemanları için global ayarı tanımla
        try {
          // Popup elementlerinin bulunacağı konteyner
          const portalContainer = this.$el.querySelector('.virtual-scroller-portals');

          if (portalContainer) {
            // Ant Design bileşenlerini yapılandırma
            this.configureAntComponents(portalContainer);

            // getPopupContainer fonksiyonunu global olarak tanımla
            window.getAntPortalContainer = () => {
              return portalContainer;
            };

            console.log('[VirtualScroller] Ant Design Portal konteyner özelleştirmesi yapıldı');
          }
        } catch (e) {
          console.error('[VirtualScroller] Ant Design Portal ayarlaması başarısız oldu:', e);
        }
      }
    },

    configureAntComponents(container) {
      // Vue prototipi üzerinden ConfigProvider ayarlarını güncelle
      if (this.$root && this.$root.$ant) {
        try {
          // Ant Design'ın ConfigProvider'ını global olarak ayarla
          const antConfigProviderOptions = {
            getPopupContainer: () => container,
            prefixCls: 'ant',
          };

          // Alt bileşenler için özel ayarlamalar
          // DatePicker, Dropdown, Tooltip, Popover vb. için
          const components = ['DatePicker', 'Calendar', 'Dropdown', 'Tooltip', 'Popover', 'Popconfirm', 'Select', 'TimePicker'];

          components.forEach(componentName => {
            if (this.$root.$ant[componentName]) {
              this.$root.$ant[componentName].props = {
                ...this.$root.$ant[componentName].props,
                getPopupContainer: {
                  type: Function,
                  default: () => container
                }
              };

              // Mevcut bileşenlerin DOM'da hızlı bir şekilde güncellenmesi için
              // Ant bileşenlerinin Props yöntemleriyle işlem yapıyoruz
              const componentInstances = document.querySelectorAll(`.ant-${componentName.toLowerCase()}`);
              componentInstances.forEach(el => {
                if (el.__vue__) {
                  el.__vue__.$props.getPopupContainer = () => container;
                }
              });
            }
          });

          console.log('[VirtualScroller] Ant Design bileşenleri yeniden yapılandırıldı');
        } catch (err) {
          console.error('[VirtualScroller] Ant Design yapılandırması başarısız oldu:', err);
        }
      }
    }
  },
  created() {
    //console.log('[VirtualScroller] created');
    // Scroll sonu için debounce metodu oluştur
    this.debouncedEndScroll = debounce(this.onEndScroll, 200);
  },
  mounted() {
    //console.log('[VirtualScroller] mounted');
    this.updateViewportHeight();
    window.addEventListener('resize', this.updateViewportHeight);

    // Başlangıç değerlerini güncelleyerek görünür öğeleri ayarlayalım
    this.$nextTick(() => {
      this.updateVisibleItems();
      this.hasInitialized = true;
      this.$emit('initialized');

      // Ant Design popup elementlerinin doğru konumlandırılması için gereken ayarı yapıyorum
      this.fixAntdPortals();
    });
  },
  updated() {
    //console.log('[VirtualScroller] updated');
    if (this.hasInitialized) {
      this.updateVisibleItems();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportHeight);
  },
  watch: {
    items: {
      handler(newItems, oldItems) {
        // Öğe listesi değiştiyse (örn. filtreleme, sıralama)
        this.$nextTick(() => {
          this.updateVisibleItems();

          // Eğer öğe sayısı değiştiyse, duruma göre scroll'u sıfırla
          if (newItems.length !== oldItems.length) {
            // Yeni liste boşsa veya eski boş listeden yeni listeye geçişse
            if (newItems.length === 0 || oldItems.length === 0) {
              this.scrollToOffset(0);
            }
          }
        });
      },
      deep: false // Sadece referans değişikliklerini izle, içerik değişikliklerini değil
    },
    itemSize() {
      this.$nextTick(this.updateVisibleItems);
    }
  }
};
</script>

<style lang="scss">
.virtual-scroller-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  /* Popup elementler için container (boş div) */
  .virtual-scroller-portals {
    position: static;
    /* static, yeni bir stacking context oluşturmaz */
  }
}

.virtual-scroller {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  .scroller-content {
    position: relative;
    width: 100%;
    overflow: visible;
    /* Taşmaya izin veriyoruz, böylece popup elementler görünür olacak */
  }

  .items-container {
    position: absolute;
    width: 100%;
    left: 0;
    /* transform KULLANILMIYOR - absolute positioning tercih edildi */
  }

  .virtual-item {
    width: 100%;
    box-sizing: border-box;
    position: relative;
    /* Position relative ekleyerek absolute konumlandırılan alt elementlerin doğru referansı almasını sağlıyorum */
  }

  .virtual-scroller-empty,
  .virtual-scroller-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 2;
  }

  .empty-placeholder,
  .loading-indicator {
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }

  .ant-calendar-picker {
    position: static !important;

    .ant-calendar-picker-container {
      position: absolute;
    }
  }
}

/* Ant Design bileşenlerini düzeltme */
.ant-calendar-picker-container,
.ant-tooltip,
.ant-dropdown,
.ant-modal-wrap,
.ant-popover {
  /* Popup elementler için z-index'i artır */
  z-index: 1050 !important;
}

/* DatePicker için özel düzeltmeler */


/* Dropdown için özel düzeltmeler */
.ant-dropdown-trigger {
  position: static !important;
}
</style>
